import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const EXPAND_CHANGES_PARAM = "expandChanges";
const HIGHLIGHT_ROW_PARAM = "highlightRow";

interface UseChangeRequestListDeepLinkOptions {
  loading: boolean;
  rowIds: string[];
}

export function useChangeRequestListDeepLink({ loading, rowIds }: UseChangeRequestListDeepLinkOptions) {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [expandChangesRowId, setExpandChangesRowId] = useState<string | null>(null);
  const [highlightRowId, setHighlightRowId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const expandChanges = searchParams.get(EXPAND_CHANGES_PARAM);
  const highlightRow = searchParams.get(HIGHLIGHT_ROW_PARAM);
  const hasTargetRow = expandChanges ? rowIds.includes(expandChanges) : true;

  useEffect(() => {
    if (!expandChanges && !highlightRow) {
      return;
    }

    if (loading) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete(EXPAND_CHANGES_PARAM);
    next.delete(HIGHLIGHT_ROW_PARAM);
    navigate({ pathname, search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });

    const rowIdToHighlight = expandChanges && hasTargetRow ? expandChanges : highlightRow;

    if (expandChanges && hasTargetRow) {
      setExpandChangesRowId(expandChanges);
      setHighlightRowId(expandChanges);
    } else if (highlightRow) {
      setExpandChangesRowId(null);
      setHighlightRowId(highlightRow);
    } else {
      setExpandChangesRowId(null);
      setHighlightRowId(null);
    }

    if (!rowIdToHighlight) {
      return;
    }

    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    }

    const timeout = setTimeout(() => {
      requestAnimationFrame(() => {
        const row = document.querySelector("tr.row-highlight") as HTMLElement | null;
        row?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }, 150);

    return () => clearTimeout(timeout);
  }, [expandChanges, hasTargetRow, highlightRow, loading, navigate, pathname, searchParams]);

  return { expandChangesRowId, highlightRowId, containerRef };
}
